<script setup lang="ts">
interface Props {
  dynamicId?: string;
  isSlot?: boolean;
  text?: string;
  icon?: string;
  color?: string;
  disabled?: boolean;
  submit?: boolean;
  spinning?: boolean;
  size?: string;
  width?: string;
}

const props = withDefaults(defineProps<Props>(), {
  dynamicId: "",
  isSlot: false,
  text: "",
  icon: "",
  color: "green",
  disabled: false,
  submit: false,
  spinning: false,
  size: "large",
  width: "10rem",
});

const emit = defineEmits<{
  clickEvent: [];
}>();

const iconUsed = computed(() => props.icon !== "");

const effectiveWidth = computed(() => {
  if (props.size === "small" && props.width === "10rem") return "7rem";
  return props.width;
});

function returnClick() {
  if (!props.disabled) {
    emit("clickEvent");
  }
}
</script>

<template>
  <button
    v-if="isSlot"
    :id="dynamicId || undefined"
    class="dynamic-slot"
    :class="[color, disabled ? 'disabled' : '']"
    :type="submit ? 'submit' : 'button'"
    @click.stop="returnClick"
    @keyup.enter="returnClick"
  >
    <slot name="button-body" />
  </button>
  <button
    v-else
    :id="dynamicId || undefined"
    :style="`min-width: ${effectiveWidth};`"
    class="dynamic-button"
    :type="submit ? 'submit' : 'button'"
    :class="{
      disabled: disabled,
      small: size === 'small',
      [color]: true,
    }"
    @click.stop="returnClick"
    @keyup.enter="returnClick"
  >
    {{ text }}
    <fa
      v-if="iconUsed"
      :icon="['fal', icon]"
      :class="spinning && 'fa-spin'"
    />
  </button>
</template>

<style lang="scss">
// Radius, tap, motion, and disabled behaviour inherit from the global
// button rule in main.scss. Here we only specify colour variants. Each
// solid variant uses color-mix() to give the hover state a faded
// version of its own colour (rather than the previous global
// brightness shift) so hovers read as a consistent "tint" family.
.dynamic-button,
.dynamic-slot {
  &:hover:not(.disabled):not(:disabled) {
    filter: none;
  }
}
.small {
  font-size: var(--text-xs);
  min-height: 2.25rem;
  padding: 0 var(--space-3);
}
// Solid fills
.green {
  background-color: var(--color-green-2);
  border-color: var(--color-green-2);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-green-2) 80%, white);
    border-color: color-mix(in srgb, var(--color-green-2) 80%, white);
  }
}
.blue {
  background-color: var(--color-blue-1);
  border-color: var(--color-blue-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-blue-1) 80%, white);
    border-color: color-mix(in srgb, var(--color-blue-1) 80%, white);
  }
}
.red {
  background-color: var(--color-red-1);
  border-color: var(--color-red-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-red-1) 80%, white);
    border-color: color-mix(in srgb, var(--color-red-1) 80%, white);
  }
}
.orange {
  background-color: var(--color-orange-1);
  border-color: var(--color-orange-1);
  // Faded orange fill on hover, but the border stays the original
  // orange-1 — gives the orange button a subtle ringed look so the
  // hover reads as 'lighter centre, same outline'.
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-orange-1) 80%, white);
    border-color: var(--color-orange-1);
  }
}
.grey {
  background-color: var(--color-grey-2);
  border-color: var(--color-grey-2);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-grey-2) 80%, white);
    border-color: color-mix(in srgb, var(--color-grey-2) 80%, white);
  }
}
.purple {
  background-color: var(--color-purple-1);
  border-color: var(--color-purple-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-purple-1) 80%, white);
    border-color: color-mix(in srgb, var(--color-purple-1) 80%, white);
  }
}
.burgandy {
  background-color: var(--color-burgandy-1);
  border-color: var(--color-burgandy-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-burgandy-1) 80%, white);
    border-color: color-mix(in srgb, var(--color-burgandy-1) 80%, white);
  }
}
.pink {
  background-color: var(--color-pink-1);
  border-color: var(--color-pink-1);
  svg {
    color: var(--color-white-1) !important;
  }
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-pink-1) 80%, white);
    border-color: color-mix(in srgb, var(--color-pink-1) 80%, white);
  }
}
.baby {
  background-color: var(--color-pink-2);
  border-color: var(--color-pink-2);
  color: var(--color-grey-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-pink-2) 80%, white);
    border-color: color-mix(in srgb, var(--color-pink-2) 80%, white);
  }
}
.yellow {
  background-color: var(--color-yellow-1);
  border-color: var(--color-yellow-1);
  color: var(--color-pink-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-yellow-1) 80%, white);
    border-color: color-mix(in srgb, var(--color-yellow-1) 80%, white);
  }
}
// Outline / transparent variants
.white {
  color: var(--color-pink-1);
  background-color: var(--color-white-1);
  border: 1px solid var(--color-pink-1);
  transition:
    background-color var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);
  svg {
    color: var(--color-pink-1) !important;
  }
  &:hover:not(.disabled):not(:disabled) {
    background-color: var(--color-pink-4);
    filter: none;
  }
}
.transparent-white {
  color: var(--color-white-1);
  background-color: transparent;
  border: 1px solid var(--color-white-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: rgba(255, 255, 255, 0.12);
    filter: none;
  }
}
.transparent-black {
  color: var(--color-grey-1);
  background-color: transparent;
  border: 1px solid var(--color-grey-3);
  &:hover:not(.disabled):not(:disabled) {
    background-color: var(--color-grey-4);
    border-color: var(--color-grey-2);
    filter: none;
  }
}
.transparent-green {
  color: var(--color-green-2);
  background-color: transparent;
  border: 1px solid var(--color-green-2);
  &:hover:not(.disabled):not(:disabled) {
    background-color: var(--color-green-3);
    filter: none;
  }
}
.transparent-pink {
  color: var(--color-pink-1);
  background-color: transparent;
  border: 1px solid var(--color-pink-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: var(--color-pink-4);
    filter: none;
  }
}
.transparent-orange {
  color: var(--color-orange-1);
  background-color: transparent;
  border: 1px solid var(--color-orange-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: color-mix(in srgb, var(--color-orange-1) 12%, white);
  }
}
.transparent {
  background: transparent;
  border: 1px solid transparent;
  color: var(--color-grey-1);
  &:hover:not(.disabled):not(:disabled) {
    background-color: var(--color-grey-4);
    filter: none;
  }
}
</style>
